import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import {
  GET_SHOP_COLLECTION,
  GET_SHOP_PRODUCTS,
} from "../../graphql/queries/shop.query";
import { GET_SHOP_COLLECTION_PRODUCTS } from "../../graphql/queries/shop.query";
import ScreenWrapper from "../../components/common/ScreenWrapper";
import Modal from "../../components/common/Modal";
import { Product } from "../../graphql/types";
import { useShop } from "../../hooks/useShop";
import {
  AddProductsToShopCollectionMutation,
  AddProductsToShopCollectionMutationVariables,
  RemoveProductFromCollectionMutation,
  RemoveProductFromCollectionMutationVariables,
} from "../../graphql/mutations/__generated__/shop.mutation";
import {
  ADD_PRODUCTS_TO_COLLECTION,
  REMOVE_PRODUCT_FROM_COLLECTION,
} from "../../graphql/mutations/shop.mutation";
import { toast } from "sonner";

function AddProductToCollection() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set()
  );
  const [currentCollectionProducts, setCurrentCollectionProducts] = useState<
    Set<string>
  >(new Set());
  const [loadingProducts, setLoadingProducts] = useState<Set<string>>(
    new Set()
  );

  const { shop } = useShop();

  // Toggle product selection in the modal
  const toggleProductSelection = (productId: string) => {
    setSelectedProducts((prev) => {
      const updated = new Set(prev);
      if (updated.has(productId)) {
        updated.delete(productId);
      } else {
        updated.add(productId);
      }
      return updated;
    });
  };

  // Mutation to add/update products in the collection
  const [addProductsToCollection] = useMutation<
    AddProductsToShopCollectionMutation,
    AddProductsToShopCollectionMutationVariables
  >(ADD_PRODUCTS_TO_COLLECTION, {
    onCompleted(data) {
      if (data.addProductsToShopCollection) {
        toast.success("Collection products updated");
        setShowProductsModal(false);
        setCurrentCollectionProducts(new Set(selectedProducts));
      }
    },
    onError: (error) =>
      toast.error(`Failed to update collection products: ${error.message}`),
    refetchQueries: [
      {
        query: GET_SHOP_COLLECTION_PRODUCTS,
        variables: { filter: { collection_id: { eq: collectionId } } },
      },
    ],
  });

  // Mutation to remove products from the collection (used for bulk updates)
  const [removeProducts] = useMutation<
    RemoveProductFromCollectionMutation,
    RemoveProductFromCollectionMutationVariables
  >(REMOVE_PRODUCT_FROM_COLLECTION, {
    onError: (error) =>
      toast.error(
        `Failed to remove products from collection: ${error.message}`
      ),
  });

  // Query to fetch collection details
  const {
    data: collectionData,
    loading: fetchLoading,
    error: fetchError,
  } = useQuery(GET_SHOP_COLLECTION, {
    variables: { filter: { id: { eq: collectionId } } },
  });

  // Query to fetch products in the collection
  const {
    data: productData,
    loading: productLoading,
    error: productError,
  } = useQuery(GET_SHOP_COLLECTION_PRODUCTS, {
    variables: { filter: { collection_id: { eq: collectionId } } },
    onCompleted: (data) => {
      const productIds = (data.getShopCollectionProducts || []).map(
        (product: Product) => product.id
      );
      setCurrentCollectionProducts(new Set(productIds));
    },
  });

  // Query to fetch all products in the shop
  const { data: shopProducts } = useQuery(GET_SHOP_PRODUCTS, {
    variables: {
      shopId: shop?.id,
    },
    skip: !shop?.id,
  });

  // Calculate which products to add and which to remove
  const saveProductSelections = () => {
    if (!collectionId) {
      toast.error("Collection ID is missing.");
      return;
    }

    // Mark all as loading during the save operation
    setLoadingProducts(new Set(selectedProducts));

    // Prepare the transaction to update the collection products
    addProductsToCollection({
      variables: {
        productIds: Array.from(selectedProducts),
        collectionId: collectionId,
      },
    }).finally(() => {
      setLoadingProducts(new Set());
    });
  };

  // Initialize selectedProducts with current collection products when modal opens
  const openProductModal = () => {
    setSelectedProducts(new Set(currentCollectionProducts));
    setShowProductsModal(true);
  };

  if (fetchLoading || productLoading) {
    return (
      <ScreenWrapper showBackBtn={true}>
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </ScreenWrapper>
    );
  }

  if (fetchError || productError) {
    return (
      <ScreenWrapper showBackBtn={true}>
        <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {fetchError ? fetchError.message : productError?.message}
        </div>
      </ScreenWrapper>
    );
  }

  const collection = collectionData?.getShopCollection;
  const products = productData?.getShopCollectionProducts || [];
  const shopProductsList = shopProducts?.getShopProducts || [];

  return (
    <ScreenWrapper showBackBtn={true}>
      <div className="max-w-6xl mx-auto md:pt-10">
        <div className="flex justify-between">
          <h1 className="text-2xl font-bold mb-6">{collection?.name}</h1>
          <button
            onClick={openProductModal}
            className="flex justify-center items-center py-3 px-4 border border-transparent text-base font-normal rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 bg-blue-500 text-white"
          >
            <span>&#43;</span>
            Manage Products
          </button>
        </div>

        {products.length > 0 ? (
          <div className="mt-6">
            <h2 className="text-xl font-medium mb-4">
            </h2>
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {products.map((product: Product) => (
                <div
                  key={product.id}
                  className="border p-4 rounded-md flex justify-between items-center"
                >
                  <div className="flex items-center">
                    {product.avatar && (
                      <img
                        src={product.avatar}
                        alt={product.title}
                        className="w-12 h-12 object-cover rounded-md mr-3"
                      />
                    )}
                    <div>
                      <p className="font-medium">{product.title}</p>
                      <div className="flex space-x-4 text-sm">
                        <p className="text-gray-500">
                          {product.currency} {product.price}
                        </p>
                        {product.profit_added != null &&
                          product.profit_added > 0 && (
                            <p className="text-green-600">
                              +{product.currency} {product.profit_added}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-10 text-center py-10 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No products in this collection yet.</p>
            <p className="text-gray-500 mt-2">
              Click "Manage Products" to add products.
            </p>
          </div>
        )}

        <Modal
          show={showProductsModal}
          setShow={setShowProductsModal}
          size={60}
        >
          <div className="flex justify-between mb-5 items-center">
            <h2 className="text-xl font-bold">Manage Collection Products</h2>
            <div className="space-x-3">
              <button
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md"
                onClick={() => setShowProductsModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-md"
                onClick={saveProductSelections}
              >
                Save Changes
              </button>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Selected {selectedProducts.size} of {shopProductsList.length}{" "}
              products
            </p>
          </div>

          {shopProductsList.length > 0 ? (
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {shopProductsList.map((product: Product) => {
                const isSelected = selectedProducts.has(product.id);
                const wasInCollection = currentCollectionProducts.has(
                  product.id
                );
                const isLoading = loadingProducts.has(product.id);

                return (
                  <div
                    key={product.id}
                    className={`border p-4 rounded-md flex justify-between items-center ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-center">
                      {product.avatar && (
                        <img
                          src={product.avatar}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded-md mr-3"
                        />
                      )}
                      <div>
                        <p className="font-medium">{product.title}</p>
                        <div className="flex space-x-4 text-sm">
                          <p className="text-gray-500">
                            {product.currency} {product.price}
                          </p>
                          {product.profit_added != null &&
                            product.profit_added > 0 && (
                              <p className="text-green-600">
                                +{product.currency} {product.profit_added}
                              </p>
                            )}
                        </div>
                      </div>
                    </div>

                    <button
                      className={`px-4 py-2 rounded-md transition-colors ${
                        isSelected
                          ? "bg-red-500 text-white"
                          : "bg-blue-500 text-white"
                      }`}
                      onClick={() => toggleProductSelection(product.id)}
                      disabled={isLoading}
                    >
                      {isLoading
                        ? "Loading..."
                        : isSelected
                        ? "Remove"
                        : "Add Product"}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No products available in this shop.
            </div>
          )}
        </Modal>
      </div>
    </ScreenWrapper>
  );
}

export default AddProductToCollection;
